import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowDown, Lightbulb, Target, Zap } from 'lucide-react';
import axios from 'axios';
import LoadingScreen from '../components/LoadingScreen';
import '../components/LoadingScreen.css';
import { analytics } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HTTP Status Codes
const HTTP_CREATED = 201;

export const Home = () => {
  const [showLoading, setShowLoading] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionsRef = useRef([]);

  useEffect(() => {
    // Always show loading screen on first visit
    // Remove sessionStorage check to ensure loading shows every time
    document.body.classList.add('loading');

    return () => {
      document.body.classList.remove('loading');
    };
  }, []);

  const handleLoadingComplete = () => {
    sessionStorage.setItem('hasSeenIntro', 'true');
    document.body.classList.remove('loading');
    setShowLoading(false);
    
    // Fade in content after loading completes
    setTimeout(() => {
      setContentVisible(true);
    }, 300);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!contentVisible) return;
    
    // Reset scroll position when content becomes visible
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      setScrollY(scrollPosition);
      setScrollProgress((scrollPosition / (documentHeight - windowHeight)) * 100);
      
      // Intersection Observer for scroll animations
      sectionsRef.current.forEach((section) => {
        if (section) {
          const rect = section.getBoundingClientRect();
          const isVisible = rect.top < windowHeight * 0.85 && rect.bottom > 0;
          if (isVisible) {
            section.classList.add('visible');
            
            // Add visible class to all animated children
            const animatedElements = section.querySelectorAll('.fade-in-left, .fade-in-right, .fade-in-up, .stagger-item, .manifesto-line-animated');
            animatedElements.forEach(el => {
              el.classList.add('visible');
            });
          }
        }
      });
    };

    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, [contentVisible]);
  // Note: All variables (scrollPosition, documentHeight, animatedElements) are local to handleScroll
  // setters (setScrollY, setScrollProgress) are stable by React guarantee

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === HTTP_CREATED) {
        // Track successful form submission
        analytics.trackContactFormSubmit(formData);
        
        toast.success('¡Mensaje enviado correctamente! Nos pondremos en contacto pronto.');
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      if (error.response) {
        toast.error(`Error: ${error.response.data.detail || 'No se pudo enviar el mensaje'}`);
      } else {
        toast.error('Error de conexión. Por favor, intenta nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    analytics.trackContactButtonClick('header');
    document.getElementById('contact-section')?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  return (
    <>
      {showLoading && <LoadingScreen onComplete={handleLoadingComplete} />}
      
      <div 
        className={`min-h-screen bg-darkBlue relative overflow-hidden transition-opacity duration-1000 ${
          contentVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ pointerEvents: showLoading ? 'none' : 'auto' }}
      >
      {/* Scroll Progress Bar */}
      <div 
        className="scroll-progress"
        style={{ width: `${scrollProgress}%` }}
      ></div>
      {/* Animated Background Elements with Enhanced Parallax */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div 
          className="absolute top-20 right-20 w-96 h-96 bg-[#00adb3]/10 rounded-full blur-3xl"
          style={{ 
            transform: `translate(${scrollY * 0.15}px, ${scrollY * 0.3}px) scale(${1 + scrollY * 0.0003})`,
            opacity: Math.max(0.3, 1 - scrollY * 0.001)
          }}
        ></div>
        <div 
          className="absolute bottom-40 left-20 w-80 h-80 bg-[#1987c9]/10 rounded-full blur-3xl"
          style={{ 
            transform: `translate(${-scrollY * 0.1}px, ${-scrollY * 0.2}px)`,
            opacity: Math.max(0.3, 1 - scrollY * 0.0008)
          }}
        ></div>
        <div 
          className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-[#00adb3]/5 rounded-full blur-3xl"
          style={{ 
            transform: `translate(-50%, -50%) translate(${scrollY * 0.05}px, ${scrollY * 0.1}px) rotate(${scrollY * 0.05}deg)`,
          }}
        ></div>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_strategic-studio/artifacts/uqsfq05c_QC%20-%20Logotipo%202da%20versio%CC%81n%20-%20FINAL%20-%20Color.svg"
            alt="Quirófano Creativo"
            className="h-[55px] transition-all duration-300 hover:scale-105"
            style={{ filter: 'brightness(0) saturate(100%) invert(67%) sepia(46%) saturate(1342%) hue-rotate(141deg) brightness(96%) contrast(101%)' }}
          />
          <Button 
            onClick={scrollToContact}
            className="cta-button-header"
          >
            Contacto
          </Button>
        </div>
      </header>

      {/* Hero Section with Enhanced Parallax */}
      <section className="min-h-screen flex items-center justify-center px-6 pt-48 pb-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-5"
          style={{ 
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            transform: `translateY(${scrollY * 0.5}px) scale(${1 + scrollY * 0.0002})` 
          }}
        ></div>
        
        <div className="max-w-6xl w-full text-center relative z-10">
          <div 
            className="mb-8 inline-block"
            style={{ 
              transform: `translateY(${scrollY * 0.2}px)`,
              opacity: Math.max(0, 1 - scrollY * 0.003)
            }}
          >
            <span className="inline-block px-6 py-2 bg-[#00adb3]/20 text-[#00adb3] border border-[#00adb3]/30 rounded-full text-sm font-light tracking-wider animate-fadeIn">
              AGENCIA CREATIVA
            </span>
          </div>
          
          <h1 
            className="hero-headline-dark mb-8"
            style={{ 
              transform: `translateY(${scrollY * 0.15}px) scale(${Math.max(0.95, 1 - scrollY * 0.0002)})`,
              opacity: Math.max(0, 1 - scrollY * 0.0025)
            }}
          >
            <span className="font-light" style={{ transform: `translateX(${-scrollY * 0.05}px)`, display: 'inline-block' }}>
              No hacemos contenido.
            </span>
            <br />
            <span className="text-gradient font-extrabold" style={{ transform: `translateX(${scrollY * 0.05}px)`, display: 'inline-block' }}>
              Construimos percepción.
            </span>
          </h1>
          
          <p 
            className="hero-subheadline-dark mb-12"
            style={{ transform: `translateY(${scrollY * 0.15}px)` }}
          >
            Estrategia antes que diseño.
          </p>
          
          <Button 
            onClick={scrollToContact}
            className="cta-button-glow group"
          >
            Conoce nuestro enfoque
            <ArrowDown className="ml-2 w-4 h-4 group-hover:translate-y-1 transition-transform" />
          </Button>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section 
        ref={addToRefs}
        className="py-32 px-6 relative section-fade"
      >
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="fade-in-left">
              <h2 className="section-headline-dark mb-8">
                <span className="font-light">La mayoría comunica</span><br />
                <span className="text-[#00adb3] font-extrabold">sin estructura</span>
              </h2>
            </div>
            <div className="space-y-8">
              <div className="problem-card stagger-item">
                <div className="problem-icon">✕</div>
                <p className="text-lg text-gray-300">Reach con <strong className="text-white">positioning</strong></p>
              </div>
              <div className="problem-card stagger-item">
                <div className="problem-icon">✕</div>
                <p className="text-lg text-gray-300">Presencia con <strong className="text-white">autoridad</strong></p>
              </div>
              <div className="problem-card stagger-item">
                <div className="problem-icon">✕</div>
                <p className="text-lg text-gray-300">Contenido con <strong className="text-white">estrategia</strong></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Thinking Section with Parallax Cards */}
      <section 
        ref={addToRefs}
        className="py-32 px-6 section-fade relative"
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="section-headline-dark mb-24 text-center fade-in-left">
            <span className="font-light">Pensamos antes de</span> <span className="text-[#1987c9] font-extrabold">diseñar</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="pillar-card-dark group stagger-item">
              <div className="pillar-icon-wrapper">
                <Lightbulb className="w-8 h-8 text-accent" />
              </div>
              <h3 className="pillar-title-dark">Estrategia</h3>
              <div className="pillar-line-glow"></div>
              <p className="pillar-description-dark">
                Definimos el problema real antes de crear soluciones. La claridad estratégica precede cualquier ejecución
              </p>
            </div>
            
            <div className="pillar-card-dark group stagger-item">
              <div className="pillar-icon-wrapper">
                <Target className="w-8 h-8 text-surgicalBlue" />
              </div>
              <h3 className="pillar-title-dark">Dirección</h3>
              <div className="pillar-line-glow"></div>
              <p className="pillar-description-dark">
                Establecemos la visión y el tono. Cada decisión creativa responde a un objetivo específico
              </p>
            </div>
            
            <div className="pillar-card-dark group stagger-item">
              <div className="pillar-icon-wrapper">
                <Zap className="w-8 h-8 text-accent" />
              </div>
              <h3 className="pillar-title-dark">Ejecución</h3>
              <div className="pillar-line-glow"></div>
              <p className="pillar-description-dark">
                Traducimos la estrategia en comunicación tangible. Diseño con propósito, no decoración
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section 
        ref={addToRefs}
        className="py-32 px-6 bg-darkBlue/50 section-fade"
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="section-headline-dark mb-24 text-center fade-in-left">
            <span className="font-light">Soluciones que</span> <span className="text-[#00adb3] font-extrabold">diseñamos</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="solution-card-dark fade-in-right stagger-item">
              <div className="solution-number">01</div>
              <h3 className="solution-title-dark">Estrategia de comunicación</h3>
              <p className="solution-description-dark">
                Construimos el marco estratégico que define cómo tu marca se comunica. Posicionamiento, mensajes clave y territorios de marca
              </p>
              <div className="solution-hover-line"></div>
            </div>
            
            <div className="solution-card-dark fade-in-right stagger-item">
              <div className="solution-number">02</div>
              <h3 className="solution-title-dark">Construcción de marca</h3>
              <p className="solution-description-dark">
                Diseñamos identidades que comunican con precisión. Desde la esencia estratégica hasta su expresión visual
              </p>
              <div className="solution-hover-line"></div>
            </div>
            
            <div className="solution-card-dark fade-in-right stagger-item">
              <div className="solution-number">03</div>
              <h3 className="solution-title-dark">Dirección creativa</h3>
              <p className="solution-description-dark">
                Establecemos la visión creativa de tus proyectos. Coherencia visual y conceptual en cada punto de contacto
              </p>
              <div className="solution-hover-line"></div>
            </div>
            
            <div className="solution-card-dark fade-in-right stagger-item">
              <div className="solution-number">04</div>
              <h3 className="solution-title-dark">Contenido estructurado</h3>
              <p className="solution-description-dark">
                Creamos contenido con arquitectura estratégica. Cada pieza responde a objetivos específicos de comunicación
              </p>
              <div className="solution-hover-line"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Method Section - Timeline Style */}
      <section 
        ref={addToRefs}
        className="py-32 px-6 section-fade"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="section-headline-dark mb-24 text-center">
            <span className="font-light">Cómo</span> <span className="text-[#1987c9] font-extrabold">trabajamos</span>.
          </h2>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-0 md:left-12 top-0 bottom-0 w-px bg-gradient-to-b from-surgicalBlue via-accent to-surgicalBlue"></div>
            
            <div className="space-y-16">
              <div className="method-step-dark">
                <div className="method-number-dark">01</div>
                <div className="method-content-dark">
                  <h3 className="method-title-dark">Diagnóstico</h3>
                  <p className="method-description-dark">
                    Analizamos tu situación actual. Identificamos problemas reales de comunicación y oportunidades estratégicas.
                  </p>
                </div>
              </div>
              
              <div className="method-step-dark">
                <div className="method-number-dark">02</div>
                <div className="method-content-dark">
                  <h3 className="method-title-dark">Estrategia</h3>
                  <p className="method-description-dark">
                    Definimos la dirección. Establecemos objetivos, mensajes clave y el territorio de marca.
                  </p>
                </div>
              </div>
              
              <div className="method-step-dark">
                <div className="method-number-dark">03</div>
                <div className="method-content-dark">
                  <h3 className="method-title-dark">Diseño</h3>
                  <p className="method-description-dark">
                    Traducimos la estrategia en expresión visual. Creamos sistemas coherentes y escalables.
                  </p>
                </div>
              </div>
              
              <div className="method-step-dark">
                <div className="method-number-dark">04</div>
                <div className="method-content-dark">
                  <h3 className="method-title-dark">Ejecución</h3>
                  <p className="method-description-dark">
                    Implementamos con precisión. Supervisamos cada detalle para mantener la coherencia estratégica.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto Section - Full Screen */}
      <section 
        ref={addToRefs}
        className="min-h-screen flex items-center justify-center px-6 py-32 relative overflow-hidden section-fade"
      >
        <div 
          className="absolute inset-0 bg-gradient-to-br from-[#123a62] via-[#00adb3]/10 to-[#123a62]"
          style={{ transform: `scale(${1 + scrollY * 0.0001})` }}
        ></div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="manifesto-text-dark">
            <p className="manifesto-line-dark manifesto-line-animated text-white" style={{ fontWeight: 300 }}>No creemos en el ruido</p>
            <p className="manifesto-line-dark manifesto-line-animated text-[#00adb3]" style={{ fontWeight: 800 }}>Creemos en la claridad</p>
            <div className="my-12"></div>
            <p className="manifesto-line-dark manifesto-line-animated text-white" style={{ fontWeight: 300 }}>No vendemos fórmulas rápidas</p>
            <p className="manifesto-line-dark manifesto-line-animated text-[#1987c9]" style={{ fontWeight: 800 }}>Diseñamos comunicación con intención</p>
            <div className="my-12"></div>
            <p className="manifesto-line-dark manifesto-line-animated text-[#00adb3] text-4xl md:text-6xl" style={{ fontWeight: 800 }}>
              La percepción correcta genera decisiones correctas
            </p>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section 
        ref={addToRefs}
        className="py-32 px-6 section-fade"
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="section-headline-dark mb-16 text-center">
            <span className="font-light">Nuestra forma de</span> <span className="text-[#1987c9] font-extrabold">pensar</span>.
          </h2>
          
          <div className="video-container-dark group">
            <div className="absolute inset-0 bg-gradient-to-tr from-[#00adb3]/20 to-[#1987c9]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-10"></div>
            <video 
              controls 
              className="relative z-20"
              preload="metadata"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_strategic-studio/artifacts/rhdvjedf_Video%20Presentacio%CC%81n%20comercial-2.mp4" 
                type="video/mp4" 
              />
              Tu navegador no soporta la reproducción de video.
            </video>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact-section" 
        ref={addToRefs}
        className="py-32 px-6 bg-gradient-to-b from-darkBlue to-black section-fade"
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-headline-dark mb-8">
              <span className="font-light">Construyamos</span> <span className="text-[#00adb3] font-extrabold">claridad</span>.
            </h2>
            <p className="text-xl text-gray-400">
              Si tu comunicación necesita estructura, podemos ayudarte.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="contact-form-dark">
            <div className="space-y-6">
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Nombre"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="contact-input-dark"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="contact-input-dark"
                />
              </div>
              <div>
                <Input
                  type="text"
                  name="company"
                  placeholder="Empresa (opcional)"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="contact-input-dark"
                />
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Cuéntanos sobre tu proyecto"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="contact-input-dark"
                />
              </div>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="cta-button-glow w-full"
              >
                {isSubmitting ? 'Enviando...' : 'Hablemos'}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_strategic-studio/artifacts/u32q43rq_QC%20-%20Logotipo%20-%20FINAL%20-%20Blanco.svg"
              alt="Quirófano Creativo"
              className="h-[60px] opacity-90 hover:opacity-100 transition-opacity"
              style={{ filter: 'brightness(0) saturate(100%) invert(67%) sepia(46%) saturate(1342%) hue-rotate(141deg) brightness(96%) contrast(101%)' }}
            />
            <p className="text-sm text-gray-400">
              © 2025 Quirófano Creativo. Estrategia antes que diseño.
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default Home;
